/**
 * COABrowser — AOS-FIN-COA-GLOBAL-01: Global Reference COA Browser
 *
 * Route: /finance/coa/reference
 * RBAC: finance.coa.read — super_admin, institution_admin, cfo,
 *       finance_manager, finance_officer, registrar
 *
 * This page is READ-ONLY. The COA reference library is a platform-global
 * resource seeded from NACUBO FARM / UGC / AICTE standards.
 *
 * BOUNDARIES (mirrors backend invariants):
 *   - No POST/PATCH/DELETE — mutation requires a versioned Alembic migration.
 *   - Oracle/SAP codes shown with a prominent "non-authoritative" disclaimer.
 *   - No institutional ledger data is shown or modified here.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Search, Filter, BookOpen, BarChart3, Tag, Circle,
  ChevronRight, X, Info, AlertTriangle, RefreshCw,
  TrendingUp, Layers, CheckCircle2, ArrowUpDown, Zap,
} from "lucide-react";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { PageHeader } from "../components/layout/Shell";

// ── RBAC guard ──────────────────────────────────────────────────────────────
const COA_READ_ROLES = new Set([
  "super_admin", "institution_admin", "cfo",
  "finance_manager", "finance_officer", "registrar",
]);

// ── Colour maps ──────────────────────────────────────────────────────────────
const TYPE_COLOURS = {
  ASSET:               "bg-emerald-900/40 text-emerald-300 border-emerald-700/40",
  LIABILITY:           "bg-red-900/40 text-red-300 border-red-700/40",
  NET_ASSET:           "bg-violet-900/40 text-violet-300 border-violet-700/40",
  OPERATING_REVENUE:   "bg-sky-900/40 text-sky-300 border-sky-700/40",
  NON_OPERATING_REVENUE: "bg-cyan-900/40 text-cyan-300 border-cyan-700/40",
  PERSONNEL_COST:      "bg-amber-900/40 text-amber-300 border-amber-700/40",
  ACADEMIC_COST:       "bg-orange-900/40 text-orange-300 border-orange-700/40",
  ADMINISTRATIVE_COST: "bg-rose-900/40 text-rose-300 border-rose-700/40",
  FINANCIAL_ITEM:      "bg-slate-700/40 text-slate-300 border-slate-600/40",
};

const CLASS_COLOURS = {
  MANDATORY:    "bg-emerald-500/10 text-emerald-400 border-emerald-500/30",
  RECOMMENDED:  "bg-sky-500/10 text-sky-400 border-sky-500/30",
  OPTIONAL:     "bg-slate-500/10 text-slate-400 border-slate-500/30",
};

const BALANCE_COLOURS = {
  DEBIT:  "text-amber-400",
  CREDIT: "text-sky-400",
};

function AccountTypeBadge({ type }) {
  const cls = TYPE_COLOURS[type] || "bg-slate-700 text-slate-300 border-slate-600";
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-md text-xs font-medium border ${cls}`}>
      {type.replace(/_/g, " ")}
    </span>
  );
}

function ClassBadge({ cls }) {
  const colours = CLASS_COLOURS[cls] || "bg-slate-700 text-slate-300 border-slate-600";
  return (
    <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium border ${colours}`}>
      {cls[0]}
    </span>
  );
}

// ── Stats bar ────────────────────────────────────────────────────────────────
function StatsBar({ stats, loading }) {
  if (loading) {
    return (
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
        {[...Array(4)].map((_, i) => (
          <div key={i} className="h-16 rounded-xl bg-slate-800/60 animate-pulse" />
        ))}
      </div>
    );
  }
  if (!stats) return null;

  const tiles = [
    { label: "Total Accounts", value: stats.total_active, icon: BookOpen, colour: "text-emerald-400", testid: "coa-stat-total" },
    { label: "MANDATORY",      value: stats.by_classification?.MANDATORY || 0, icon: CheckCircle2, colour: "text-emerald-400", testid: "coa-stat-mandatory" },
    { label: "Student Finance",value: stats.mandatory_for_student_finance, icon: TrendingUp, colour: "text-sky-400", testid: "coa-stat-student-finance" },
    { label: "Account Types",  value: Object.keys(stats.by_account_type || {}).length, icon: Layers, colour: "text-violet-400", testid: "coa-stat-account-types" },
  ];

  return (
    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
      {tiles.map((t) => (
        <div key={t.label} data-testid={t.testid} className="flex items-center gap-3 p-4 rounded-xl bg-slate-800/50 border border-slate-700/50">
          <t.icon className={`h-5 w-5 shrink-0 ${t.colour}`} />
          <div>
            <p className="text-lg font-bold text-slate-100 leading-none">{t.value}</p>
            <p className="text-xs text-slate-500 mt-0.5">{t.label}</p>
          </div>
        </div>
      ))}
    </div>
  );
}

// ── Account detail panel ──────────────────────────────────────────────────────
function AccountDetailPanel({ account, onClose }) {
  const navigate = useNavigate();
  if (!account) return null;

  // Roles that can request activation
  const token = localStorage.getItem("academiaos_token");
  let canActivate = false;
  try {
    const payload = JSON.parse(atob(token.split(".")[1]));
    canActivate = ["super_admin", "institution_admin", "cfo", "finance_manager"].includes(payload.role);
  } catch { /**/ }

  return (
    <div
      data-testid="coa-detail-panel"
      className="fixed inset-y-0 right-0 z-50 w-full max-w-md bg-slate-900 border-l border-slate-700 shadow-2xl flex flex-col"
    >
      {/* Header */}
      <div className="flex items-center justify-between p-5 border-b border-slate-700/60">
        <div>
          <p className="text-xs text-slate-500 font-mono mb-0.5">{account.reference_code}</p>
          <h3 className="text-base font-semibold text-slate-100 leading-tight">{account.account_name}</h3>
        </div>
        <button
          onClick={onClose}
          data-testid="coa-detail-close"
          className="p-1.5 rounded-lg hover:bg-slate-700 transition-colors text-slate-400 hover:text-slate-200"
        >
          <X className="h-4 w-4" />
        </button>
      </div>

      {/* Badges */}
      <div className="flex flex-wrap gap-2 px-5 py-3 border-b border-slate-700/40">
        <AccountTypeBadge type={account.account_type} />
        <span className={`text-xs font-semibold px-2 py-0.5 rounded border ${CLASS_COLOURS[account.classification]}`}>
          {account.classification}
        </span>
        <span className={`text-xs font-mono font-bold ${BALANCE_COLOURS[account.normal_balance]}`}>
          {account.normal_balance} balance
        </span>
      </div>

      {/* Body */}
      <div className="flex-1 overflow-y-auto p-5 space-y-5">
        <Section label="Category">
          <p className="text-sm text-slate-300">{account.account_category}</p>
          <p className="text-xs text-slate-500">{account.account_subcategory}</p>
        </Section>

        {account.description && (
          <Section label="Description">
            <p className="text-sm text-slate-300 leading-relaxed">{account.description}</p>
          </Section>
        )}

        {account.academic_context && (
          <Section label="Academic Context">
            <p className="text-sm text-slate-400 leading-relaxed">{account.academic_context}</p>
          </Section>
        )}

        <Section label="Module Requirements">
          <ul className="space-y-1">
            <FlagRow label="Student Finance" active={account.is_mandatory_for_student_finance} />
            <FlagRow label="Payroll"          active={account.is_mandatory_for_payroll} />
            <FlagRow label="Hostel"           active={account.is_mandatory_for_hostel} />
          </ul>
        </Section>

        {(account.oracle_suggestion || account.sap_suggestion) && (
          <Section label="ERP Reference">
            <div className="flex items-start gap-2 p-3 rounded-lg bg-amber-900/20 border border-amber-700/30 mb-2">
              <AlertTriangle className="h-3.5 w-3.5 text-amber-400 mt-0.5 shrink-0" />
              <p className="text-xs text-amber-300 leading-relaxed">
                Non-authoritative reference only. Do not use these codes as institutional GL account numbers.
              </p>
            </div>
            {account.oracle_suggestion && (
              <p className="text-xs text-slate-400">Oracle suggestion: <span className="font-mono text-slate-300">{account.oracle_suggestion}</span></p>
            )}
            {account.sap_suggestion && (
              <p className="text-xs text-slate-400">SAP suggestion: <span className="font-mono text-slate-300">{account.sap_suggestion}</span></p>
            )}
          </Section>
        )}

        <Section label="Provenance">
          <p className="text-xs text-slate-500 font-mono leading-relaxed">{account.source_provenance}</p>
          <p className="text-xs text-slate-600 mt-1">Template v{account.template_version}</p>
        </Section>
      </div>

      {/* Activate footer — COA-02 */}
      {canActivate && (
        <div className="p-5 border-t border-slate-700/60">
          <button
            onClick={() => {
              onClose();
              navigate(`/finance/coa/activations?ref=${account.id}&code=${account.reference_code}`);
            }}
            data-testid="coa-activate-btn"
            className="w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg bg-primary text-primary-foreground hover:bg-primary/90 transition-colors text-sm font-medium"
          >
            <Zap className="h-4 w-4" />
            Activate in Institution
          </button>
          <p className="text-xs text-slate-500 text-center mt-2">
            Submits a request for independent review (maker-checker).
          </p>
        </div>
      )}
    </div>
  );
}

function Section({ label, children }) {
  return (
    <div>
      <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">{label}</p>
      {children}
    </div>
  );
}

function FlagRow({ label, active }) {
  return (
    <li className="flex items-center gap-2 text-xs">
      <span className={`h-1.5 w-1.5 rounded-full ${active ? "bg-emerald-400" : "bg-slate-600"}`} />
      <span className={active ? "text-slate-300" : "text-slate-600"}>{label}</span>
      {active && <span className="text-emerald-500 text-xs ml-auto">Required</span>}
    </li>
  );
}

// ── Account row ──────────────────────────────────────────────────────────────
function AccountRow({ account, selected, onClick }) {
  return (
    <tr
      data-testid={`coa-row-${account.reference_code}`}
      onClick={onClick}
      className={`
        cursor-pointer border-b border-slate-700/40 text-sm
        transition-colors hover:bg-slate-800/60
        ${selected ? "bg-slate-800/80" : ""}
      `}
    >
      <td className="px-4 py-3 font-mono text-xs text-slate-400 whitespace-nowrap">{account.reference_code}</td>
      <td className="px-4 py-3 text-slate-200 max-w-xs">
        <span className="line-clamp-1">{account.account_name}</span>
        <span className="text-xs text-slate-500 block">{account.account_subcategory}</span>
      </td>
      <td className="px-4 py-3 hidden md:table-cell">
        <AccountTypeBadge type={account.account_type} />
      </td>
      <td className={`px-4 py-3 text-xs font-mono font-semibold hidden sm:table-cell ${BALANCE_COLOURS[account.normal_balance]}`}>
        {account.normal_balance}
      </td>
      <td className="px-4 py-3">
        <ClassBadge cls={account.classification} />
      </td>
      <td className="px-4 py-3 text-slate-500">
        <ChevronRight className="h-3.5 w-3.5" />
      </td>
    </tr>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────
const PAGE_SIZE = 50;

const ACCOUNT_TYPES = [
  "ASSET", "LIABILITY", "NET_ASSET", "OPERATING_REVENUE", "NON_OPERATING_REVENUE",
  "PERSONNEL_COST", "ACADEMIC_COST", "ADMINISTRATIVE_COST", "FINANCIAL_ITEM",
];

const CLASSIFICATIONS = ["MANDATORY", "RECOMMENDED", "OPTIONAL"];

export default function COABrowser() {
  const { user } = useAuth();

  // ── state ──────────────────────────────────────────────────────────────────
  const [accounts, setAccounts]       = useState([]);
  const [total, setTotal]             = useState(0);
  const [totalPages, setTotalPages]   = useState(0);
  const [page, setPage]               = useState(1);
  const [loading, setLoading]         = useState(false);
  const [error, setError]             = useState(null);

  const [search, setSearch]           = useState("");
  const [filterType, setFilterType]   = useState("");
  const [filterCls, setFilterCls]     = useState("");

  const [stats, setStats]             = useState(null);
  const [statsLoading, setStatsLoading] = useState(false);

  const [selected, setSelected]       = useState(null);

  const canAdmin = user?.role === "super_admin" || user?.role === "institution_admin";
  const canRead  = COA_READ_ROLES.has(user?.role);

  // Debounced search
  const debounceRef = useRef(null);
  const [debouncedSearch, setDebouncedSearch] = useState("");
  useEffect(() => {
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => setDebouncedSearch(search), 350);
    return () => clearTimeout(debounceRef.current);
  }, [search]);

  // ── fetch accounts ─────────────────────────────────────────────────────────
  const fetchAccounts = useCallback(() => {
    if (!canRead) return;
    setLoading(true);
    setError(null);
    const params = new URLSearchParams({
      page: String(page),
      page_size: String(PAGE_SIZE),
      active_only: "true",
    });
    if (debouncedSearch) params.set("search", debouncedSearch);
    if (filterType)      params.set("account_type", filterType);
    if (filterCls)       params.set("classification", filterCls);

    api.get(`/finance/coa/reference-accounts?${params}`)
      .then((r) => {
        setAccounts(r.data.items);
        setTotal(r.data.total);
        setTotalPages(r.data.total_pages);
      })
      .catch((e) => setError(e?.response?.data?.detail || "Failed to load accounts"))
      .finally(() => setLoading(false));
  }, [canRead, page, debouncedSearch, filterType, filterCls]);

  useEffect(() => { fetchAccounts(); }, [fetchAccounts]);

  // Reset to page 1 when filters change
  useEffect(() => {
    setPage(1);
    setSelected(null);
  }, [debouncedSearch, filterType, filterCls]);

  // ── fetch stats ────────────────────────────────────────────────────────────
  useEffect(() => {
    if (!canAdmin) return;
    setStatsLoading(true);
    api.get("/finance/coa/reference-accounts/stats")
      .then((r) => setStats(r.data))
      .catch(() => setStats(null))
      .finally(() => setStatsLoading(false));
  }, [canAdmin]);

  // ── access denied ──────────────────────────────────────────────────────────
  if (!canRead) {
    return (
      <div data-testid="coa-access-denied" className="flex flex-col items-center justify-center min-h-[40vh] text-center p-8">
        <AlertTriangle className="h-8 w-8 text-amber-400 mb-3" />
        <h2 className="text-lg font-semibold text-slate-200 mb-1">Access Restricted</h2>
        <p className="text-sm text-slate-500 max-w-md">
          Browsing the Chart of Accounts reference library requires the
          <code className="mx-1 px-1.5 py-0.5 bg-slate-800 rounded text-xs font-mono">finance.coa.read</code>
          permission. Contact your Finance administrator.
        </p>
      </div>
    );
  }

  return (
    <div data-testid="coa-browser-page" className="space-y-5">
      <PageHeader
        title="COA Reference Library"
        description={`Global Academic Chart of Accounts — ${total || 343} canonical accounts · Template v1.0`}
        action={
          <div className="flex items-center gap-2 text-xs text-slate-500 bg-slate-800/60 px-3 py-1.5 rounded-lg border border-slate-700/50">
            <Info className="h-3.5 w-3.5 text-sky-400 shrink-0" />
            Read-only reference library. No institutional ledger changes.
          </div>
        }
      />

      {/* Stats (admin only) */}
      {canAdmin && <StatsBar stats={stats} loading={statsLoading} />}

      {/* Controls */}
      <div
        data-testid="coa-filter-bar"
        className="flex flex-col sm:flex-row gap-3 items-start sm:items-center"
      >
        {/* Search */}
        <div className="relative flex-1 min-w-0">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-500" />
          <input
            data-testid="coa-search-input"
            type="text"
            placeholder="Search by code or account name…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full pl-9 pr-4 py-2 bg-slate-800/60 border border-slate-700/50 rounded-lg text-sm text-slate-200 placeholder-slate-600 focus:outline-none focus:border-sky-500/60 focus:ring-1 focus:ring-sky-500/20"
          />
          {search && (
            <button onClick={() => setSearch("")} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-300">
              <X className="h-3.5 w-3.5" />
            </button>
          )}
        </div>

        {/* Type filter */}
        <select
          data-testid="coa-filter-type"
          value={filterType}
          onChange={(e) => setFilterType(e.target.value)}
          className="px-3 py-2 bg-slate-800/60 border border-slate-700/50 rounded-lg text-sm text-slate-300 focus:outline-none focus:border-sky-500/60 cursor-pointer"
        >
          <option value="">All account types</option>
          {ACCOUNT_TYPES.map((t) => (
            <option key={t} value={t}>{t.replace(/_/g, " ")}</option>
          ))}
        </select>

        {/* Classification filter */}
        <select
          data-testid="coa-filter-classification"
          value={filterCls}
          onChange={(e) => setFilterCls(e.target.value)}
          className="px-3 py-2 bg-slate-800/60 border border-slate-700/50 rounded-lg text-sm text-slate-300 focus:outline-none focus:border-sky-500/60 cursor-pointer"
        >
          <option value="">All classifications</option>
          {CLASSIFICATIONS.map((c) => (
            <option key={c} value={c}>{c}</option>
          ))}
        </select>

        {/* Clear filters */}
        {(filterType || filterCls) && (
          <button
            data-testid="coa-clear-filters"
            onClick={() => { setFilterType(""); setFilterCls(""); }}
            className="flex items-center gap-1.5 px-3 py-2 text-xs text-slate-400 hover:text-slate-200 bg-slate-800/40 border border-slate-700/40 rounded-lg transition-colors"
          >
            <X className="h-3.5 w-3.5" /> Clear
          </button>
        )}
      </div>

      {/* Result count */}
      <div className="flex items-center justify-between text-xs text-slate-500">
        <span data-testid="coa-result-count">
          {loading ? "Loading…" : `${total} account${total !== 1 ? "s" : ""} found`}
          {(search || filterType || filterCls) && " (filtered)"}
        </span>
        {total > 0 && (
          <span>Page {page} of {totalPages}</span>
        )}
      </div>

      {/* Error */}
      {error && (
        <div data-testid="coa-error" className="flex items-center gap-2 p-4 rounded-lg bg-red-950/30 border border-red-800/40 text-red-300 text-sm">
          <AlertTriangle className="h-4 w-4 shrink-0" />
          {error}
        </div>
      )}

      {/* Table */}
      <div className="overflow-x-auto rounded-xl border border-slate-700/50 bg-slate-900/50">
        <table data-testid="coa-table" className="w-full">
          <thead>
            <tr className="border-b border-slate-700/60 bg-slate-800/40">
              <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">Code</th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">Account Name</th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider hidden md:table-cell">Type</th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider hidden sm:table-cell">Balance</th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">Class</th>
              <th className="px-4 py-3 w-10"></th>
            </tr>
          </thead>
          <tbody>
            {loading && accounts.length === 0 ? (
              [...Array(8)].map((_, i) => (
                <tr key={i} className="border-b border-slate-700/30">
                  {[...Array(6)].map((_, j) => (
                    <td key={j} className="px-4 py-3">
                      <div className="h-4 bg-slate-800 rounded animate-pulse" />
                    </td>
                  ))}
                </tr>
              ))
            ) : accounts.length === 0 && !loading ? (
              <tr>
                <td colSpan={6} className="px-4 py-12 text-center text-sm text-slate-500">
                  No accounts match your filters.
                </td>
              </tr>
            ) : (
              accounts.map((a) => (
                <AccountRow
                  key={a.id}
                  account={a}
                  selected={selected?.id === a.id}
                  onClick={() => setSelected(selected?.id === a.id ? null : a)}
                />
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div data-testid="coa-pagination" className="flex items-center justify-center gap-2">
          <button
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            disabled={page <= 1}
            className="px-3 py-1.5 text-xs rounded-lg border border-slate-700/50 text-slate-400 hover:text-slate-200 hover:border-slate-600 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
          >
            Previous
          </button>
          <span className="text-xs text-slate-500">
            {page} / {totalPages}
          </span>
          <button
            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            disabled={page >= totalPages}
            className="px-3 py-1.5 text-xs rounded-lg border border-slate-700/50 text-slate-400 hover:text-slate-200 hover:border-slate-600 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
          >
            Next
          </button>
        </div>
      )}

      {/* Non-authoritative ERP disclaimer */}
      <div className="flex items-start gap-2 p-3 rounded-lg bg-amber-900/10 border border-amber-700/20">
        <Info className="h-3.5 w-3.5 text-amber-500 mt-0.5 shrink-0" />
        <p className="text-xs text-amber-500/80 leading-relaxed">
          <span className="font-semibold">Non-authoritative reference.</span> Oracle and SAP account code
          suggestions shown in account details are informational only and MUST NOT be used as institutional
          GL account codes. Source: NACUBO FARM, UGC Guidelines, AICTE Financial Standards, Ind AS.
        </p>
      </div>

      {/* Detail panel */}
      {selected && (
        <>
          <div
            className="fixed inset-0 z-40 bg-black/40 backdrop-blur-sm"
            onClick={() => setSelected(null)}
          />
          <AccountDetailPanel account={selected} onClose={() => setSelected(null)} />
        </>
      )}
    </div>
  );
}
